import { Component } from '@angular/core';
import { ApplicationService } from '../../../../services/application/application.service';
import { NotificationService } from '../../../../services/notification/notification.service';
import { ApplicationResponse } from '../../../../models/applicationResponse';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Timestamp } from 'firebase/firestore';
import { switchMap } from 'rxjs';
import { NotificationRequest } from '../../../../models/notification-request';
import { SharedService } from '../../../../services/shared/shared.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { RouterModule } from '@angular/router';
import Swal from 'sweetalert2';
import { EmailService } from '../../../../services/email/email.service';

@Component({
  selector: 'app-applications',
  imports: [CommonModule, FormsModule, RouterModule],
  templateUrl: './applications.component.html',
  styleUrl: './applications.component.css'
})
export class ApplicationsComponent {
  applications: ApplicationResponse[] = [];
  showSpinner = true;

  constructor(
    private applicationService: ApplicationService,
    private notificationService: NotificationService,
    private sharedService: SharedService,
    private spinner: NgxSpinnerService,
    private emailService: EmailService
  ) {}

  ngOnInit() {
    this.applicationService.getAllApplicationsWithDetails().subscribe({
      next: (apps) => {
        this.applications = apps;
        this.showSpinner = false;
      },
      error: (err) => {
        this.sharedService.handleError(err, 'error', 'Fehler beim Laden der Bewerbungen. Bitte versuchen Sie es erneut.');
        this.showSpinner = false;
      }
    });
  }

  updateApplicationStatus(app: ApplicationResponse, newStatus: 'pending' | 'reviewed' | 'accepted' | 'rejected') {
    this.spinner.show();
    const request = new NotificationRequest();
    request.id = this.generateId();
    request.userIds = [app.user.id];
    request.title = `Ihre Bewerbung für ${app.job.title} wurde ${this.translateStatus(newStatus)}.`;
    request.body = this.getNotificationBody(newStatus, app.job.title);
    request.type = this.getNotificationType(newStatus);
    request.timestamp = Timestamp.now();
    request.status = 'UNREAD';
    request.isOpen = false;
    request.isNew = true;
    let emailRequest = {
      to_name: app.user.fullName,
      reply_to: app.user.email,
      subject: request.title,
      body: request.body
    };

    this.applicationService.updateApplicationStatus(app.id!, newStatus).pipe(
      switchMap(() => this.notificationService.saveNotification(request))
    ).subscribe({
      next: () => {
        this.applications = this.applications.map(application =>
          application.id === app.id
            ? { ...application, status: newStatus }
            : application
        );

        this.emailService.sendEmail(emailRequest);

        this.sharedService.handleSuccess('', `Bewerbungsstatus auf ${this.translateStatus(newStatus)} aktualisiert`);
        this.spinner.hide();
      },
      error: (error) => {
        this.sharedService.handleError(error, 'error', 'Fehler beim Aktualisieren des Bewerbungsstatus. Bitte versuchen Sie es erneut.');
        this.spinner.hide();
      }
    });
  }

  private getNotificationBody(status: 'pending' | 'reviewed' | 'accepted' | 'rejected', jobTitle: string): string {
    switch (status) {
      case 'pending':
        return `Ihre Bewerbung für ${jobTitle} ist in Prüfung. Wir benachrichtigen Sie, sobald sie bearbeitet wurde.`;
      case 'reviewed':
        return `Ihre Bewerbung für ${jobTitle} wurde geprüft. Bitte überprüfen Sie weitere Anweisungen oder Updates.`;
      case 'accepted':
        return `Herzlichen Glückwunsch! Ihre Bewerbung für ${jobTitle} wurde angenommen. Bitte fahren Sie mit den nächsten Schritten fort.`;
      case 'rejected':
        return `Es tut uns leid, Ihnen mitteilen zu müssen, dass Ihre Bewerbung für ${jobTitle} abgelehnt wurde. Vielen Dank für Ihre Bewerbung.`;
      default:
        return `Der Status Ihrer Bewerbung für ${jobTitle} wurde auf ${this.translateStatus(status)} aktualisiert.`;
    }
  }

  private translateStatus(status: 'pending' | 'reviewed' | 'accepted' | 'rejected'): string {
    switch (status) {
      case 'pending':
        return 'Ausstehend';
      case 'reviewed':
        return 'Geprüft';
      case 'accepted':
        return 'Angenommen';
      case 'rejected':
        return 'Abgelehnt';
      default:
        return status;
    }
  }

  private getNotificationType(status: 'pending' | 'reviewed' | 'accepted' | 'rejected'): NotificationRequest['type'] {
    switch (status) {
      case 'pending':
        return 'APPLICATION_PENDING';
      case 'reviewed':
        return 'APPLICATION_REVIEWED';
      case 'accepted':
        return 'APPLICATION_ACCEPTED';
      case 'rejected':
        return 'APPLICATION_REJECTED';
      default:
        return 'APPLICATION_STATUS_UPDATE'; // Fallback
    }
  }

  confirmDelete(appId: string) {
    Swal.fire({
      title: 'Sind Sie sicher?',
      text: 'Möchten Sie diese Bewerbung löschen? Diese Aktion kann nicht rückgängig gemacht werden.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Ja, löschen!',
      cancelButtonText: 'Abbrechen'
    }).then((result) => {
      if (result.isConfirmed) {
        this.deleteApplication(appId);
      }
    });
  }

  deleteApplication(appId: string) {
    this.spinner.show();
    const app = this.applications.find(a => a.id === appId);
    this.applicationService.deleteApplication(appId).subscribe({
      next: () => {
        this.applications = this.applications.filter(app => app.id !== appId);
        this.sharedService.handleSuccess('', 'Bewerbung erfolgreich gelöscht');
        this.spinner.hide();
      },
      error: (error) => {
        this.sharedService.handleError(error, 'error', 'Fehler beim Löschen der Bewerbung. Bitte versuchen Sie es erneut.');
        this.spinner.hide();
      }
    });
  }

  private generateId(): string {
    return Math.random().toString(36).substr(2, 9); // Simple ID generator; replace with Firestore ID if needed
  }
}